/**
 * Google Tag Manager ecommerce events for the online shop. Event names and the
 * `ecommerce` payload follow GA4's recommended ecommerce schema, so a GA4 tag
 * with "Send Ecommerce data" reads them without any mapping.
 */
import { unitPriceFor } from "@/app/[tenant]/shop/cart-line-utils";

import {
  type PurchaseCharges,
  type ShopCartLine,
  type ShopItem,
  isCustomAmountGiftCard,
} from "@/types/shop";

import { formatToHumanReadable } from "./format-data";
import { pushGTMEvent } from "./gtm-events";

const CURRENCY = "USD";
const CUSTOM_GIFT_CARD_ITEM_ID = "custom_gift_card";

interface GTMEcommerceItem {
  item_id: string;
  item_name: string;
  item_category: string;
  item_category2?: string;
  price: number;
  quantity: number;
}

const centsToDollars = (cents: number) => Math.round(cents) / 100;

const lineToItem = (
  line: ShopCartLine,
  quantity = line.quantity,
): GTMEcommerceItem => ({
  item_id: "itemId" in line ? line.itemId : CUSTOM_GIFT_CARD_ITEM_ID,
  item_name: line.snapshot.name,
  item_category: formatToHumanReadable(line.itemType),
  ...(line.snapshot.category && { item_category2: line.snapshot.category }),
  price: centsToDollars(unitPriceFor(line)),
  quantity,
});

const cartValue = (items: GTMEcommerceItem[]) =>
  Math.round(
    items.reduce((sum, item) => sum + item.price * item.quantity, 0) * 100,
  ) / 100;

/**
 * GTM merges every push into one data model, so an earlier `ecommerce` object
 * would leak into this event's items unless it is cleared first.
 */
const pushEcommerceEvent = (
  eventName: string,
  ecommerce: Record<string, unknown>,
) => {
  if (typeof window === "undefined" || !window.dataLayer) return;
  window.dataLayer.push({ ecommerce: null });
  pushGTMEvent(eventName, { ecommerce: { currency: CURRENCY, ...ecommerce } });
};

/** Patient opened an item to configure or add it. */
export const trackShopViewItem = (item: ShopItem) => {
  const priceCents = item.displayPrice ?? item.price;
  pushEcommerceEvent("view_item", {
    value: centsToDollars(priceCents),
    items: [
      {
        item_id: isCustomAmountGiftCard(item)
          ? CUSTOM_GIFT_CARD_ITEM_ID
          : item.id,
        item_name: item.name,
        item_category: formatToHumanReadable(item.type),
        ...(item.category && { item_category2: item.category }),
        price: centsToDollars(priceCents),
        quantity: 1,
      },
    ],
  });
};

export const trackShopAddToCart = (
  line: ShopCartLine,
  quantity = line.quantity,
) => {
  const items = [lineToItem(line, quantity)];
  pushEcommerceEvent("add_to_cart", { value: cartValue(items), items });
};

export const trackShopRemoveFromCart = (
  line: ShopCartLine,
  quantity = line.quantity,
) => {
  const items = [lineToItem(line, quantity)];
  pushEcommerceEvent("remove_from_cart", { value: cartValue(items), items });
};

export const trackShopBeginCheckout = (lines: ShopCartLine[]) => {
  const items = lines.map((line) => lineToItem(line));
  pushEcommerceEvent("begin_checkout", { value: cartValue(items), items });
};

/** Patient chose how the order reaches them: a clinic pickup or shipping. */
export const trackShopAddShippingInfo = (
  lines: ShopCartLine[],
  shippingTier: "Pickup" | "Shipping",
) => {
  const items = lines.map((line) => lineToItem(line));
  pushEcommerceEvent("add_shipping_info", {
    value: cartValue(items),
    shipping_tier: shippingTier,
    items,
  });
};

/**
 * The conversion event. GA4 wants `value` to be merchandise only, with tax and
 * shipping as their own fields; `value` is still after promotions, so it can be
 * below the sum of item prices.
 */
export const trackShopPurchase = ({
  transactionId,
  lines,
  totalCents,
  charges,
}: {
  transactionId: string;
  lines: ShopCartLine[];
  totalCents: number;
  charges: PurchaseCharges;
}) => {
  pushEcommerceEvent("purchase", {
    transaction_id: transactionId,
    value: centsToDollars(
      Math.max(0, totalCents - charges.taxCents - charges.shippingCents),
    ),
    tax: centsToDollars(charges.taxCents),
    shipping: centsToDollars(charges.shippingCents),
    items: lines.map((line) => lineToItem(line)),
  });
};
